// KindergartenDetail.jsx
import React, { useEffect, useState } from 'react';
import { statisticsApi, resolveBenefitsParam } from './statisticsApi';
import { useDragScroll } from './hooks/useDragScroll';

const AGE_GROUPS = [
  { ageChild: 0, code: 1970, label: '0-1 рік' },
  { ageChild: 1, code: 1971, label: '1-2 роки' },
  { ageChild: 2, code: 1972, label: '2-3 роки' },
  { ageChild: 3, code: 1973, label: '3-4 роки' },
  { ageChild: 4, code: 1974, label: '4-5 років' },
  { ageChild: 5, code: 1975, label: '5-6 років' },
  { ageChild: 6, code: 1976, label: '6-7 років' },
  { ageChild: 7, code: 1977, label: '7-8 років' },
  { ageChild: 8, code: 1978, label: '8+ років' },
];

// Форматирование даты
const fmtDate = (iso) => {
  if (!iso) return '—';

  if(iso.match(/\*/)){
    const [yy,mm,dd] = iso.split('-') || '';

    if(yy && mm && dd) return `${dd}.${mm}.${yy}`;
    
    return '—';
  }
  
  const d = new Date(iso);
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Вычисляем возрастную группу по ageChild (>= 8 → последняя)
const getGroupByAge = (ageChild) => {
  const idx = Math.min(ageChild, 8);
  return AGE_GROUPS[idx];
};

// ── Таблица заявок одной возрастной группы ────────────────────────────────────
const AgeGroupTable = ({ group, requests }) => {
  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Заголовок группы */}
      <button
        onClick={() => setExpanded(p => !p)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#f0f4ff', border: '1px solid #c8d8ff',
          borderRadius: expanded ? '8px 8px 0 0' : 8,
          padding: '8px 14px', width: '100%', cursor: 'pointer',
          fontWeight: 600, fontSize: '0.9rem', color: '#1e40af',
          transition: 'border-radius 0.15s',
        }}
      >
        <span style={{
          display: 'inline-block', width: 28, height: 28, borderRadius: '50%',
          background: '#0069ff', color: '#fff', fontSize: '0.78rem', fontWeight: 700,
          lineHeight: '28px', textAlign: 'center', flexShrink: 0,
        }}>
          {requests.length}
        </span>
        {group.label}
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>
          {expanded ? '▲ згорнути' : '▼ розгорнути'}
        </span>
      </button>

      {expanded && (
        <div
          ref={scrollRef}
          style={{ overflowX: 'auto', cursor: 'grab', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}
          {...dragListeners}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={thSt}>#</th>
                <th style={thSt}>ID заявки</th>
                <th style={{ ...thSt, textAlign: 'left' }}>Свідоцтво</th>
                <th style={thSt}>Дата нар.</th>
                <th style={thSt}>Вік</th>
                <th style={thSt}>Бажана дата</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req, idx) => (
                <tr key={req._id} style={{ borderBottom: '1px solid #f1f3f4', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={tdCt}>{idx + 1}</td>
                  <td style={{ ...tdCt, fontFamily: 'monospace', fontSize: '0.73rem', color: '#6b7280' }}>
                    {req._id.slice(-8)}
                  </td>
                  <td style={{ ...tdSt, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {req.child?.birthCertificate?.number || '—'}
                  </td>
                  <td style={tdCt}>{fmtDate(req.child?.birthDate)}</td>
                  <td style={tdCt}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px',
                      background: '#eef2ff', borderRadius: 10,
                      color: '#1e40af', fontWeight: 600, fontSize: '0.78rem',
                    }}>
                      {req.ageChild} р.
                    </span>
                  </td>
                  <td style={{ ...tdCt, color: req.preferDateFrom ? '#1f2937' : '#9ca3af' }}>
                    {fmtDate(req.preferDateFrom)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thSt = { padding: '8px 10px', fontWeight: 600, color: '#374151', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '2px solid #e5e7eb' };
const tdCt = { padding: '7px 10px', textAlign: 'center', verticalAlign: 'middle' };
const tdSt = { padding: '7px 10px', textAlign: 'left', verticalAlign: 'middle' };

// ── Основной компонент ────────────────────────────────────────────────────────
const KindergartenDetail = ({ kgId, kgNumber, district, year, filters, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grouped, setGrouped] = useState([]); // [{ group, requests }]
  const [totalCount, setTotalCount] = useState(0);
  const [activeGroupIdx, setActiveGroupIdx] = useState(null); // для навигации по табам
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const yearNum = parseInt(year.split('→')[0].trim());
        const apiFilters = {
          kyiv_reg: filters.registration === 'kyiv_reg' ? 'kyiv_reg' : 'all',
          kyiv_mr: filters.municipalCard === 'kyiv_mr_parent' ? 'kyiv_mr_parent' : 'all',
          benefits: resolveBenefitsParam(filters.benefit),
          reserv: filters.invitations || 'all',
          in_kg: filters.enrolled || 'all',
          req_crt: filters.created || 'all',
        };
        const resp = await statisticsApi.getKindergartenDetail(yearNum, kgId, apiFilters);
        const detail = resp.data?.requestsDetail?.filter(r=>r.ageChild >= 0 ).filter(r=>r.ageChild < 8) || [];

        // Группируем по возрасту
        const byAge = {};
        detail.forEach(req => {
          const g = getGroupByAge(req.ageChild ?? 0);
          if (!byAge[g.ageChild]) byAge[g.ageChild] = [];
          byAge[g.ageChild].push(req);
        });

        // Сортируем по возрастной группе, внутри — по дате рождения
        const result = AGE_GROUPS
          .filter(g => byAge[g.ageChild]?.length)
          .map(g => ({
            group: g,
            requests: (byAge[g.ageChild] || []).sort((a, b) =>
              new Date(a.child?.birthDate || 0) - new Date(b.child?.birthDate || 0)
            ),
          }));

        setGrouped(result);
        setTotalCount(detail.length);
      } catch (e) {
        setError(e.message || 'Помилка завантаження');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [kgId, year, filters]);

  return (
    <div style={{ padding: '12px 16px' }}>
      {/* Заголовок панели */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
            ЗДО № {kgNumber}
            <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280', fontSize: '0.9rem' }}>{district}</span>
          </h4>
          {!loading && !error && (
            <div style={{ marginTop: 6, fontSize: '0.82rem', color: '#6b7280' }}>
              Всього заявок: <strong style={{ color: '#1f2937' }}>{totalCount}</strong>
              {' · '}
              Вікових груп: <strong style={{ color: '#1f2937' }}>{grouped.length}</strong>
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: '#f1f3f4', border: 'none', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer', fontSize: 16,
            color: '#6b7280', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Закрити"
        >✕</button>
      </div>

      {/* Быстрая навигация по группам */}
      {!loading && !error && grouped.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {grouped.map(({ group, requests }, idx) => (
            <button
              key={group.ageChild}
              onClick={() => {
                setActiveGroupIdx(idx);
                // Скролл к нужной группе через data-атрибут
                document.getElementById(`kg-age-group-${kgId}-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }}
              style={{
                padding: '4px 12px', borderRadius: 20, border: '1px solid #c8d8ff',
                background: activeGroupIdx === idx ? '#0069ff' : '#f0f4ff',
                color: activeGroupIdx === idx ? '#fff' : '#1e40af',
                fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {group.label} <span style={{ fontWeight: 700 }}>({requests.length})</span>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280' }}>
          <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
          Завантаження заявок…
        </div>
      )}

      {error && (
        <div style={{ color: '#dc2626', padding: '12px 0', fontSize: '0.9rem' }}>
          Помилка: {error}
        </div>
      )}

      {!loading && !error && grouped.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af' }}>
          Заявок за обраними фільтрами не знайдено
        </div>
      )}

      {!loading && !error && grouped.map(({ group, requests }, idx) => (
        <div key={group.ageChild} id={`kg-age-group-${kgId}-${idx}`}>
          <AgeGroupTable group={group} requests={requests} />
        </div>
      ))}
    </div>
  );
};

export default KindergartenDetail;